"use client";

import React, { useRef } from "react";
import { Search, MapPin, Star, FileText, TrendingUp, Clock, CircleCheck as CheckCircle2, ArrowRight, UserCheck } from 'lucide-react';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const methodologySteps = [{
  id: "step-1",
  stepNumber: "01",
  title: "Audit & Competitive Analysis",
  timeline: "Weeks 1–2",
  description: "A granular analysis covering current local search visibility, target service locations, local keyword gaps, and local map pack competitor positioning.",
  iconName: "Search",
  deliverables: ["Geo-grid ranking benchmark & search radius map", "Competitor map pack audit & gap analysis", "Local keyword mapping & buyer-intent research"]
}, {
  id: "step-2",
  stepNumber: "02",
  title: "GMB & Citation Cleanup",
  timeline: "Weeks 3–4",
  description: "I execute comprehensive Google Business Profile SEO, fully auditing and optimizing your listing alongside correcting inconsistent NAP (Name, Address, Phone) citations across key directories.",
  iconName: "MapPin",
  deliverables: ["Complete Google Business Profile optimization", "Inconsistent NAP citation audit & clean-up", "High-authority local directory submissions"]
}, {
  id: "step-3",
  stepNumber: "03",
  title: "Review Strategy & Automation",
  timeline: "Weeks 5–6",
  description: "I design a sustainable review generation workflow that consistently builds customer trust, improves map pack rankings, and converts local searchers.",
  iconName: "Star",
  deliverables: ["Automated Google review acquisition strategy", "Review response templates & sentiment guidance", "Conversion-focused local badge setup"]
}, {
  id: "step-4",
  stepNumber: "04",
  title: "Content & Link Building",
  timeline: "Months 2–3",
  description: "I craft location-focused landing pages and secure genuine local editorial links to solidify your authority in specific geographical service areas.",
  iconName: "FileText",
  deliverables: ["Hyper-targeted local service area pages", "On-page local schema markup implementation", "Targeted outreach for genuine local backlinks"]
}, {
  id: "step-5",
  stepNumber: "05",
  title: "Monitoring & Optimisation",
  timeline: "Ongoing",
  description: "I continuously track map pack positions, phone calls, direction requests, and organic conversions, refining strategies to maintain top ranking positions.",
  iconName: "TrendingUp",
  deliverables: ["Monthly transparent performance dashboard", "Geo-grid ranking progress tracking", "Direct monthly strategy call with Yassine"]
}];
export default function Localseomethodology() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.from(".methodology-header", {
        opacity: 0,
        y: -24,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 80%"
        }
      });
      gsap.from(".step-card", {
        opacity: 0,
        y: 36,
        stagger: 0.15,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".steps-container",
          start: "top 75%"
        }
      });
      gsap.from(".consultant-banner", {
        opacity: 0,
        scale: 0.96,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".consultant-banner",
          start: "top 85%"
        }
      });
    });
  }, {
    scope: containerRef
  });
  const getStepIcon = (iconName: string) => {
    switch (iconName) {
      case "Search":
        return <Search className="size-6 text-primary" />;
      case "MapPin":
        return <MapPin className="size-6 text-primary" />;
      case "Star":
        return <Star className="size-6 text-primary" />;
      case "FileText":
        return <FileText className="size-6 text-primary" />;
      case "TrendingUp":
        return <TrendingUp className="size-6 text-primary" />;
      default:
        return <Search className="size-6 text-primary" />;
    }
  };
  return <section id="localseomethodology" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="methodology-header max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 border border-border/50 text-foreground">
            <UserCheck className="size-3.5 mr-1.5 text-primary" />
            My Approach
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold font-serif tracking-tight text-foreground">
            My Local SEO Methodology
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            A structured, repeatable local search roadmap designed to capture high-intent customers in your service area and drive verified local revenue growth.
          </p>
        </div>

        <div className="steps-container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {methodologySteps.map((step, i) => <div key={step.id} data-index={i} className="step-card h-full">
              <Card className="h-full border border-border shadow-xs hover:shadow-md transition-shadow duration-300 bg-card text-card-foreground flex flex-col justify-between">
                <div>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-3xl font-bold font-mono text-primary/40">
                        {step.stepNumber}
                      </span>
                      <Badge variant="outline" className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 border-border">
                        <Clock className="size-3 text-primary" />
                        {step.timeline}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2.5 rounded-lg bg-secondary/60 shrink-0">
                        {getStepIcon(step.iconName)}
                      </div>
                      <CardTitle className="text-xl font-semibold text-foreground font-serif">
                        {step.title}
                      </CardTitle>
                    </div>

                    <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
                      {step.description}
                    </CardDescription>
                  </CardHeader>

                  <Separator className="my-2" />

                  <CardContent className="pt-4">
                    <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">
                      Key Deliverables
                    </p>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {step.deliverables.map((item, idx) => <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </div>
              </Card>
            </div>)}

          <div data-index={5} className="step-card h-full">
            <Card className="h-full border border-primary/20 bg-muted/40 text-card-foreground shadow-xs flex flex-col justify-between p-6 rounded-xl">
              <div className="space-y-4">
                <Badge variant="default" className="bg-primary text-primary-foreground">
                  How I Work
                </Badge>
                
                <h3 className="text-xl sm:text-2xl font-semibold font-serif text-foreground">
                  Methodology Built for Consistent Local Growth
                </h3>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  Rigorous execution across every audit, citation fix, and local optimization delivers technical precision and sustainable map pack ranking gains.
                </p>

                <div className="pt-2 space-y-2 text-sm font-medium text-foreground">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary" />
                    <span>Clear, honest reporting at every stage</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary" />
                    <span>Clear Timelines & Action Items</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary" />
                    <span>Focused on Local Revenue & Leads</span>
                  </div>
                </div>
              </div>

              <div className="pt-6">
                <Button asChild size="lg" className="w-full justify-between group">
                  <Link to="/ContactPage">
                    <span>Book a Free Local SEO Consultation</span>
                    <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
            </Card>
          </div>
        </div>

        <div className="consultant-banner bg-card border border-border rounded-xl p-6 sm:p-8 shadow-xs max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant" loading="lazy" decoding="async" className="size-16 rounded-full object-cover border-2 border-primary/20 shrink-0" />
            <div>
              <p className="text-base font-semibold text-foreground font-serif">
                Ready to dominate local search in your target area?
              </p>
              <p className="text-sm text-muted-foreground">
                Get a customized local SEO strategy tailored specifically to your business goals.
              </p>
            </div>
          </div>

          <Button asChild variant="outline" size="default" className="shrink-0">
            <Link to="/CaseStudiesPage">
              <span>View Verified Local Results</span>
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}